// Player names.
// - Display form: NFKC, invisible / format characters removed, whitespace collapsed and trimmed.
// - Comparison key: the display form case-folded, without accents (Latin, Greek; loose marks on Cyrillic;
//   other scripts' marks are kept: they are part of the spelling), with lookalikes from other scripts
//   (Cyrillic, Greek, Armenian, Cherokee, Latin small capitals) mapped to Latin, dash variants unified. A name has a SET of keys
//   (usernameKeys: one per way of reading it, Cyrillic or Latin); uniqueness is enforced on every key (unique
//   "UsernameKeys"."key"), and two names clash when any of their keys are equal. So "Bob", "BOB", "Ｂｏｂ", "Bob\u200B", "Bób" and "Вob" (Cyrillic В) are the same name.
// - Guest-like names ("guest" + digit or separator, compared on the key) are reserved for guest identities.

export const USERNAME_MAX = 64;

// Zero-width / format characters (\p{Cf}: ZWSP, ZWJ, ZWNJ, BOM, bidi controls, soft hyphen…), control
// characters, and other characters that render as nothing: combining grapheme joiner, Hangul fillers,
// Mongolian free variation selectors, variation selectors, braille blank.
// eslint-disable-next-line no-misleading-character-class -- deliberate: each combining/variation code point is listed on its own (u flag)
const INVISIBLE = /[\p{Cf}\p{Cc}\u034F\u115F\u1160\u17B4\u17B5\u180B-\u180F\u3164\uFFA0\uFE00-\uFE0F\u2800\u{E0100}-\u{E01EF}]/gu;

export function normaliseUsername(raw: unknown): string {
    return String(raw ?? "")
        .normalize("NFKC")
        .replace(/\s+/g, " ")          // before stripping, so tabs/newlines become spaces
        .replace(INVISIBLE, "")
        .replace(/\s+/g, " ")
        .trim()
        .normalize("NFKC");
}

// Comparison key. Order matters: NFKC, then case folding, then accents stripped, then lookalikes mapped.
// The lookalike map is applied to case-folded text only, so every capital and its small letter always get
// the same key ("ИВАН" = "Иван", "ΝΙΚΟΣ" = "Νίκος").
function caseFold(s: string): string {
    return s.toLowerCase().toUpperCase().toLowerCase();   // close to full case folding (ß → ss, ς → σ, İ → i̇)
}
// Non-spacing and enclosing combining marks.
const COMBINING = /[\p{Mn}\p{Me}]/gu;
// A base character with the combining marks that follow it (or marks with no base at all).
const CLUSTER = /\P{M}\p{M}*|\p{M}+/gu;
// Scripts where a combining mark is an accent on a letter ("José" = "Jose", "Νίκος" = "Νικος"), plus marks on
// digits, punctuation, symbols and spaces. In other scripts marks are part of the spelling (Devanagari and
// Thai vowel signs, the anusvara in "सिंह", Japanese dakuten...) and are kept: "कुमार" ≠ "कमार".
const ACCENTED_SCRIPT = /^[\p{Script=Latin}\p{Script=Greek}\p{Script=Common}\p{Script=Inherited}]/u;
const CYRILLIC = /^\p{Script=Cyrillic}/u;

/**
 * Case-folded, without accents. Cyrillic letters that carry a mark as part of the letter (й, ё, ї, ў, ӂ,
 * ѓ, ќ, ӣ, ӯ, ӧ, ӱ…: precomposed letters in Unicode) are letters in their own right and are kept, so
 * "Андрей" ≠ "Андреи"; only loose marks on Cyrillic (a stress accent typed on a vowel) are dropped.
 */
function fold(s: string): string {
    return caseFold(s).normalize("NFC").replace(CLUSTER, c => {
        if (/^\p{M}/u.test(c)) return "";                                  // marks with nothing to sit on
        if (CYRILLIC.test(c)) return Array.from(c)[0];                     // the (precomposed) letter, no loose marks
        if (ACCENTED_SCRIPT.test(c)) return c.normalize("NFD").replace(COMBINING, "");
        return c;                                                           // other scripts: unchanged
    });
}
// For the reserved-name check only: every mark stripped, in every script (a guest lookalike can't hide
// behind a mark that is significant elsewhere).
function foldAll(s: string): string {
    return caseFold(s).normalize("NFKD").replace(/\p{M}/gu, "");      // spacing marks (Mc) too
}

// Letters from other scripts that look like a Latin letter, in the spirit of the Unicode TR39 "skeleton".
// Listed in either case; the table is built on the case-folded form (first entry wins), so a capital and
// its small letter always map to the same Latin letter. Where they look like different Latin letters
// (Greek Ν/ν: N/v), the capital's lookalike is used for the key and the small letter's shape is also
// checked for reserved names (SHAPES below).
export const LOOKALIKES = [
    // Cyrillic
    "Аa", "Вb", "Еe", "Ѕs", "Іi", "Јj", "Кk", "Мm", "Нh", "Оo", "Рp", "Сc", "Тt", "Уy", "Хx",
    "Ԁd", "Ԛq", "Ԝw", "Ӏl", "Ѵv",
    // Greek
    "Αa", "Βb", "Εe", "Ζz", "Ηh", "Ιi", "Κk", "Μm", "Νn", "Οo", "Ρp", "Τt", "Υy", "Χx", "Ϲc", "Ϳj",
    // Armenian (small letters are the lookalikes)
    "սu", "օo", "ցg", "հh", "ոn", "Տs", "Օo", "Լl",
    // Cherokee (capitals; their small letters fold to them)
    "Ꭺa", "Ᏼb", "Ꮯc", "Ꭰd", "Ꭼe", "Ꮐg", "Ꮋh", "Ꭵi", "Ꭻj", "Ꮶk", "Ꮮl", "Ꮇm", "Ꮑn", "Ꮎo", "Ꮲp", "Ꭱr",
    "Ꮪs", "Ꭲt", "Ꮩv", "Ꮃw", "Ꮓz",
    // Latin small capitals and other Latin variants
    "ᴀa", "ʙb", "ᴄc", "ᴅd", "ᴇe", "ꜰf", "ɢg", "ʜh", "ɪi", "ᴊj", "ᴋk", "ʟl", "ᴍm", "ɴn", "ᴏo", "ᴘp", "ʀr",
    "ꜱs", "ᴛt", "ᴜu", "ᴠv", "ᴡw", "ʏy", "ᴢz", "ıi", "ɑa", "ȷj", "ɡg",
];
// Used only for the reserved-name check (they can't go in the key without merging two letters of the same
// script, which are different letters to their readers): small-letter shapes that differ from the
// capital's (Greek ν/υ/η/μ/ω, Cyrillic п/г), and second lookalikes of a Latin letter within one script
// (Cyrillic Ү and Һ next to У and Н, Greek γ next to Υ, Armenian ռ next to ո, Cherokee Ꮤ next to Ꮃ).
// No two letters of one script may map to the same Latin letter in LOOKALIKES (tested).
const SHAPES = ["νv", "υu", "ηn", "μu", "ωw", "пn", "гr", "Үy", "Һh", "γy", "ռn", "Ꮤw"];

function buildMap(pairs: string[], base: Record<string, string> = {}) {
    const map: Record<string, string> = {...base};
    for (const p of pairs) {
        const [src, dst] = Array.from(p);
        for (const k of Array.from(new Set([caseFold(src), caseFold(src.toUpperCase()), caseFold(src.toLowerCase())]))) {
            if (Array.from(k).length === 1 && !(k in map) && k !== dst) map[k] = dst;
        }
    }
    return map;
}
const SKELETON = buildMap(LOOKALIKES);
const SHAPE_SKELETON = buildMap(LOOKALIKES, buildMap(SHAPES));

function skeleton(folded: string, map: Record<string, string>) {
    return Array.from(folded, c => map[c] ?? c).join("");
}

function finish(s: string) {
    return s.normalize("NFKC").replace(/[\p{Pd}\u2212\u2043]/gu, "-");     // all dashes/hyphens count as "-"
}

/**
 * Primary comparison key of a name (the round-6 key, as in 5214558): NFKC, invisible characters removed,
 * case-folded, accents stripped ("Guést" = "Guest"; see fold for which marks), lookalikes from other scripts
 * mapped to Latin ("Guеst" with a Cyrillic е = "Guest"), all dashes as "-". Cyrillic letters like ё, ї, й
 * are kept whole, so "Сёма" ≠ "Сема" and "Сёмa" (Latin a) = "Сёма". The display name is unchanged.
 * Stored in "Users"."usernameKey". Uniqueness is enforced on ALL keys of a name (usernameKeys).
 */
export function usernameKey(raw: unknown): string {
    return finish(skeleton(fold(normaliseUsername(raw)), SKELETON));
}

/**
 * All reservation keys of a name. Two names clash when ANY key of one equals ANY key of the other (each key is
 * stored in "UsernameKeys" with a unique index). A name can be read in more than one script, so it gets one key
 * per reading:
 *  - the primary key (usernameKey): Cyrillic letters with a mark are letters in their own right
 *    (Ёлка ≠ Елка), Latin letters in a Cyrillic word count as their Cyrillic lookalikes (Сёмa = Сёма);
 *  - the Cyrillic reading: in a word that contains Cyrillic, a Latin accented letter that has a Cyrillic twin
 *    counts as that twin (ë → ё, ï → ї, ö → ӧ...), so "Joëл" = "Joёл";
 *  - the Latin reading (twinKey): in a word that mixes Cyrillic with another script, a Cyrillic accented letter
 *    counts as its Latin twin, so "Joё" = "Joë" = "Joe", "Bjӧrn" = "Björn"; a lone Cyrillic letter in a name
 *    that has other-script letters too ("Jo ё", "Jo-ё", "Jo_ё") is read the same way.
 * й never maps (no Latin twin): Andreй ≠ Andrei, Андрей ≠ Андреи.
 */
export function usernameKeys(raw: unknown): string[] {
    const n = normaliseUsername(raw);
    const keys = [
        finish(skeleton(fold(n), SKELETON)),
        finish(skeleton(fold(latinTwinsToCyrillic(n)), SKELETON)),
        twinKeyOf(n),
    ];
    return Array.from(new Set(keys));
}

/** The Latin reading of a name (see usernameKeys). */
export function twinKey(raw: unknown): string {
    return twinKeyOf(normaliseUsername(raw));
}
function twinKeyOf(n: string): string {
    return finish(skeleton(mixedScriptWords(fold(n)), SKELETON));
}

/** True when two names may not both exist (any of their keys are equal). */
export function usernamesClash(a: unknown, b: unknown): boolean {
    const kb = new Set(usernameKeys(b));
    return usernameKeys(a).some(k => kb.has(k));
}

// Second lookalikes of Cyrillic letters, safe only where no other Cyrillic letter can be confused with them.
const CYRILLIC_TWIN: Record<string, string> = {"\u04AF": "y", "\u04BB": "h"};   // ү, һ
const LETTER = /\p{L}/u;
const WORD = /[\p{L}\p{M}]+/gu;

/** The Cyrillic base of an accented Cyrillic letter whose base looks Latin (ё → е, ї → і, ќ → к), else null. */
function cyrillicTwinBase(c: string): string | null {
    const base = c.normalize("NFD").replace(COMBINING, "");
    return base !== c && Array.from(base).length === 1 && SKELETON[base] ? base : null;
}
/** A Cyrillic letter that can be read as a Latin one (а, с, ё, ќ, ү...); л, ж, и, й... can't. */
function latinReadable(c: string): boolean {
    return !!(SKELETON[c] || CYRILLIC_TWIN[c] || cyrillicTwinBase(c));
}

// Latin accented letter (case-folded, NFC) -> its Cyrillic twin: ë -> ё, ï -> ї, ö -> ӧ, ḱ -> ќ, y̆ -> ў...
// Built from Unicode: every Cyrillic letter = base + marks where the base has a Latin lookalike.
const LATIN_TO_CYRILLIC: Record<string, string> = (() => {
    const map: Record<string, string> = {};
    for (let cp = 0x400; cp <= 0x52F; cp++) {
        const c = caseFold(String.fromCodePoint(cp)).normalize("NFC");
        if (Array.from(c).length !== 1 || !CYRILLIC.test(c)) continue;
        const d = c.normalize("NFD"), base = Array.from(d)[0], marks = d.slice(base.length);
        if (!marks || !SKELETON[base]) continue;
        const latin = (SKELETON[base] + marks).normalize("NFC");
        if (!(latin in map)) map[latin] = c;
    }
    return map;
})();

/** The Cyrillic reading: in words that contain Cyrillic, Latin accented letters become their Cyrillic twins. */
function latinTwinsToCyrillic(n: string): string {
    return caseFold(n).normalize("NFC").replace(WORD, word => {
        if (!Array.from(word).some(c => CYRILLIC.test(c))) return word;
        return word.replace(CLUSTER, c => {
            if (!/^\p{Script=Latin}/u.test(c)) return c;
            return LATIN_TO_CYRILLIC[c.normalize("NFC")] ?? c;
        });
    });
}

/**
 * The Latin reading (round 7/8). Inside a pure Cyrillic word, letters like ё, ї, ӧ, ӓ, ѐ are their own letters
 * (Ёлка ≠ Елка, Май ≠ Маи), so fold keeps them. But in a word that mixes Cyrillic with another script ("Joё":
 * Latin J, o + Cyrillic ё), the Cyrillic letters may only stand in for the Latin ones they look like: ё is
 * compared as its base е (= Latin e), so "Joё" = "Joë" = "Joe". Only letters whose base has a Latin lookalike
 * are mapped; й (base и) stays й. Ү and Һ count as y and h there.
 * A word with a Cyrillic letter that can't be read as Latin at all (л, ж, и, д...: "Ёлкa", "Алёнa") is not a Latin
 * lookalike, so it keeps its Cyrillic letters (its Cyrillic reading covers it); otherwise "Ёлкa" would also
 * take the key of the different name "Елка".
 * A lone Cyrillic letter ("Jo ё", "Jo-ё", "Jo1ё") is mapped too when the name has letters of another script.
 */
function mixedScriptWords(folded: string): string {
    const otherScript = Array.from(folded).some(c => LETTER.test(c) && !CYRILLIC.test(c));
    return folded.replace(WORD, word => {
        const letters = Array.from(word).filter(c => LETTER.test(c));
        const cyr = letters.filter(c => CYRILLIC.test(c));
        if (cyr.length === 0) return word;
        const lone = letters.length === 1 && otherScript;
        if (cyr.length === letters.length && !lone) return word;
        if (!cyr.every(latinReadable)) return word;
        return Array.from(word, c => {
            if (!CYRILLIC.test(c)) return c;
            if (CYRILLIC_TWIN[c]) return CYRILLIC_TWIN[c];
            return cyrillicTwinBase(c) ?? c;
        }).join("");
    });
}

/** Returns an error message, or null when the (normalised) name is acceptable. */
export function usernameProblem(name: string): string | null {
    const n = normaliseUsername(name);
    if (n.length < 1 || Array.from(n).length > USERNAME_MAX || n.length > USERNAME_MAX * 2) return `Player name must be 1-${USERNAME_MAX} characters`;
    if (n !== name) return "Player name contains invalid characters";
    if (isReservedUsername(n)) return "Names like \"Guest-1\", \"Guest 1\" or \"guest1\" are reserved for guest players";
    return null;
}

// "guest" on its own, or followed by a digit, separator, punctuation or symbol ("Guest-1", "Guest 1",
// "Guest_1", "Guest.1", "guest1", "Guest#1"...), is reserved. Words that merely start with it
// ("Guesty", "Guestroom") are fine.
const RESERVED = /^guest(?:$|[\p{N}\p{P}\p{S}\p{Z}\s])/u;

export function isReservedUsername(name: string): boolean {
    const folded = foldAll(normaliseUsername(name));
    // every key, and the same with small-letter shapes (Greek υ looks like u, while its capital Υ looks like Y)
    return usernameKeys(name).some(k => RESERVED.test(k)) || RESERVED.test(finish(skeleton(folded, SHAPE_SKELETON)));
}
